import { mediaQueryLarge, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

export default class ResultsList extends PaginatedList {

  #gridObserver = null;
  #isReorderingFeatured = false;
  #reorderTimer = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener(
      'change',
      this.#handleMediaQueryChange
    );

    this.setAttribute('initialized', '');

    const viewport = mediaQueryLarge.matches
      ? 'desktop'
      : 'mobile';

    sessionStorage.removeItem(
      `product-grid-view-${viewport}`
    );

    const targetElement = mediaQueryLarge.matches
      ? this.querySelector(
          '[data-grid-layout="desktop-default-option"]'
        )
      : this.querySelector(
          '[data-grid-layout="mobile-option"]'
        );

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
    }

    this.#setLayout('default');

    /*
     * Watch Shopify product/filter changes.
     */
    this.#setupGridObserver();

    /*
     * Initial featured layout.
     */
    requestAnimationFrame(() => {
      this.reorderFeaturedProducts();
    });
  }


  disconnectedCallback() {

    mediaQueryLarge.removeEventListener(
      'change',
      this.#handleMediaQueryChange
    );

    this.#gridObserver?.disconnect();

    clearTimeout(this.#reorderTimer);
  }


  /**
   * ============================================================
   * GRID LAYOUT SELECTOR
   * ============================================================
   */

  updateLayout({ target }) {

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    this.#animateLayoutChange(target.value);
  }


  /**
   * ============================================================
   * ANIMATE LAYOUT CHANGE
   * ============================================================
   */

  #animateLayoutChange = async (value) => {

    const { grid } = this.refs;

    if (!grid) {
      return;
    }

    await startViewTransition(
      () => this.#setLayout(value),
      ['product-grid']
    );
  };


  /**
   * ============================================================
   * SET GRID LAYOUT
   * ============================================================
   */

  #setLayout(value) {

    const { grid } = this.refs;

    if (!grid) {
      return;
    }

    grid.setAttribute(
      'product-grid-view',
      value
    );

    /*
     * Rebuild after layout changes.
     */
    requestAnimationFrame(() => {
      this.reorderFeaturedProducts();
    });
  }


  /**
   * ============================================================
   * GRID OBSERVER
   * ============================================================
   *
   * Watches Shopify filtering/sorting changes.
   *
   * We do NOT immediately rebuild on every mutation.
   * A short debounce prevents repeated rebuilds and jumping.
   * ============================================================
   */

  #setupGridObserver() {

    const { grid } = this.refs;

    if (!grid || !window.MutationObserver) {
      return;
    }

    this.#gridObserver?.disconnect();

    this.#gridObserver =
      new MutationObserver(() => {

        /*
         * Ignore mutations caused by our own
         * featured-product insertion/removal.
         */
        if (this.#isReorderingFeatured) {
          return;
        }

        clearTimeout(this.#reorderTimer);

        this.#reorderTimer = setTimeout(() => {

          requestAnimationFrame(() => {

            this.reorderFeaturedProducts();

          });

        }, 100);

      });


    this.#gridObserver.observe(
      grid,
      {
        childList: true,
        subtree: true
      }
    );
  }


  /**
   * ============================================================
   * REBUILD FEATURED PRODUCTS
   * ============================================================
   *
   * DESKTOP / 3 COLUMN:
   *
   * Product 1 | Product 2 | Product 3
   * FEATURED
   *
   * Product 4 | Product 5 | Product 6
   * FEATURED
   *
   * Product 7 | Product 8 | Product 9
   * FEATURED
   *
   *
   * ZOOM OUT / 2 COLUMN:
   *
   * Product 1 | Product 2
   * FEATURED
   *
   * Product 3 | Product 4
   * FEATURED
   *
   * Product 5 | Product 6
   * FEATURED
   *
   *
   * The featured product is selected only from products
   * where data-featured="true".
   * ============================================================
   */

reorderFeaturedProducts() {

  const { grid } = this.refs;

  if (!grid) {
    return;
  }

  /*
   * Prevent multiple rebuilds at the same time.
   */
  if (this.#isReorderingFeatured) {
    return;
  }

  this.#isReorderingFeatured = true;

  /*
   * Stop observer while we modify the grid.
   */
  this.#gridObserver?.disconnect();

  try {

    /*
     * ========================================================
     * STEP 1
     * Remove previously created featured rows.
     * ========================================================
     */

    grid
      .querySelectorAll(
        ':scope > [data-featured-inserted="true"]'
      )
      .forEach((item) => {
        item.remove();
      });


    /*
     * ========================================================
     * STEP 2
     * Get all ORIGINAL Shopify products.
     * ========================================================
     */

    const allProducts = [
      ...grid.querySelectorAll(
        ':scope > .product-grid__item'
      )
    ].filter((item) => {

      return !item.hasAttribute(
        'data-featured-inserted'
      );

    });


    if (!allProducts.length) {
      return;
    }


    /*
     * ========================================================
     * STEP 3
     * Reset product styles/classes.
     *
     * IMPORTANT:
     * Featured products that were previously hidden
     * are made visible again.
     * ========================================================
     */

    allProducts.forEach((item) => {

      item.classList.remove(
        'featured-grid-item',
        'featured-grid-item--inline',
        'featured-grid-item--full'
      );

      item.style.gridColumn = '';
      item.style.gridColumnStart = '';
      item.style.gridColumnEnd = '';
      item.style.width = '';
      item.style.display = '';

    });


    /*
     * ========================================================
     * STEP 4
     * Separate FEATURED and NORMAL products.
     * ========================================================
     */

    const featuredProducts =
      allProducts.filter((item) => {

        return item.dataset.featured === 'true';

      });


    const normalProducts =
      allProducts.filter((item) => {

        return item.dataset.featured !== 'true';

      });


    /*
     * ========================================================
     * STEP 5
     * If there are no featured products,
     * leave the normal product grid untouched.
     * ========================================================
     */

    if (!featuredProducts.length) {
      return;
    }


    /*
     * ========================================================
     * STEP 6
     * Determine layout.
     *
     * Desktop:
     * 3 NORMAL products
     * then 1 FEATURED
     *
     * Zoom-out:
     * 2 NORMAL products
     * then 1 FEATURED
     * ========================================================
     */

    const layout =
      grid.getAttribute(
        'product-grid-view'
      );

    const isZoomOut =
      layout === 'zoom-out';


    const normalProductsPerBlock =
      isZoomOut ? 2 : 3;


    /*
     * ========================================================
     * STEP 7
     * Keep track of featured products that are actually
     * converted into featured rows.
     *
     * IMPORTANT:
     *
     * We DO NOT hide all featured products here.
     *
     * A featured product will only be hidden if it is
     * successfully used in a featured row.
     * ========================================================
     */

    const usedFeaturedProducts = new Set();


    /*
     * ========================================================
     * STEP 8
     * Insert featured products after groups of NORMAL
     * products.
     * ========================================================
     */

    let featuredIndex = 0;

    let normalCount = 0;


    normalProducts.forEach((normalProduct) => {

      normalCount++;


      /*
       * Wait until we have the required number
       * of NORMAL products.
       */
      if (
        normalCount <
        normalProductsPerBlock
      ) {
        return;
      }


      /*
       * Get next unused featured product.
       */
      const featuredProduct =
        featuredProducts[featuredIndex];


      /*
       * No unused featured products left.
       */
      if (!featuredProduct) {
        return;
      }


      /*
       * ======================================================
       * CREATE NEW FULL-WIDTH FEATURED ROW
       * ======================================================
       */

      const featuredClone =
        document.createElement('li');


      featuredClone.className =
        'product-grid__item featured-grid-item featured-grid-item--full';


      featuredClone.setAttribute(
        'data-featured-inserted',
        'true'
      );


      /*
       * Store which original product this featured row
       * belongs to.
       */
      featuredClone.setAttribute(
        'data-featured-product-id',
        featuredProduct.dataset.productId || ''
      );


      /*
       * Force full width.
       */
      featuredClone.style.gridColumn =
        '1 / -1';

      featuredClone.style.gridColumnStart =
        '1';

      featuredClone.style.gridColumnEnd =
        '-1';

      featuredClone.style.width =
        '100%';


      /*
       * ======================================================
       * Get ONLY the featured layout.
       * ======================================================
       */

      const featuredContent =
        featuredProduct.querySelector(
          '.collection-product-featured'
        );


      /*
       * If featured content doesn't exist,
       * DO NOT consume this featured product.
       */
      if (!featuredContent) {
        return;
      }


      /*
       * Clone only the featured section.
       */
      const featuredContentClone =
        featuredContent.cloneNode(true);


      featuredContentClone.style.display =
        'block';

      featuredContentClone.style.width =
        '100%';


      featuredClone.appendChild(
        featuredContentClone
      );


      /*
       * ======================================================
       * INSERT FEATURED ROW
       * ======================================================
       */

      normalProduct.after(
        featuredClone
      );


      /*
       * ======================================================
       * Mark this featured product as USED.
       * ======================================================
       */

      usedFeaturedProducts.add(
        featuredProduct
      );


      /*
       * Hide ONLY the original product that was actually
       * inserted as a featured row.
       */
      featuredProduct.style.display =
        'none';


      /*
       * Move to next featured product.
       */
      featuredIndex++;


      /*
       * Reset normal counter.
       */
      normalCount = 0;

    });


    /*
     * ========================================================
     * STEP 9
     * IMPORTANT:
     *
     * Any featured product that was NOT used in a featured
     * row remains visible as a NORMAL product.
     *
     * This solves the 5-product / 2-featured-product issue.
     * ========================================================
     */

    featuredProducts.forEach((featuredProduct) => {

      if (
        !usedFeaturedProducts.has(
          featuredProduct
        )
      ) {

        /*
         * Not enough normal products yet.
         *
         * Keep it visible as a normal product.
         */
        featuredProduct.style.display = '';

      }

    });
    /*
 * ========================================================
 * STEP 10
 * Add class when the inserted featured product is the
 * LAST CHILD of the grid.
 * ========================================================
 */

grid
  .querySelectorAll(
    ':scope > .product-grid__item[data-featured-inserted="true"]'
  )
  .forEach((item) => {

    item.classList.remove(
      'featured-last-child'
    );

    if (
      item === grid.lastElementChild
    ) {
      item.classList.add(
        'featured-last-child'
      );
    }

  });


  } finally {

    /*
     * Re-enable rebuilding.
     */
    this.#isReorderingFeatured = false;


    /*
     * Reconnect observer.
     */
    if (this.#gridObserver) {

      const { grid: currentGrid } =
        this.refs;

      if (currentGrid) {

        this.#gridObserver.observe(
          currentGrid,
          {
            childList: true,
            subtree: true
          }
        );

      }

    }

  }

}


  /**
   * ============================================================
   * HANDLE DESKTOP / MOBILE BREAKPOINT
   * ============================================================
   */

  #handleMediaQueryChange = (event) => {

    const targetElement = event.matches
      ? this.querySelector(
          '[data-grid-layout="desktop-default-option"]'
        )
      : this.querySelector(
          '[data-grid-layout="mobile-option"]'
        );

    if (!(targetElement instanceof HTMLInputElement)) {
      return;
    }

    targetElement.checked = true;

    this.#setLayout('default');
  };

}


if (!customElements.get('results-list')) {

  customElements.define(
    'results-list',
    ResultsList
  );

}